import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../Axios";

// Fetch all products with query params
export const fetchProducts = createAsyncThunk(
  "products/fetchProducts",
  async ({ page = 1, limit = 10, category = "", search = "" }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/product/getAllProducts`, {
        params: { page, limit, category, search },
      });
      return response.data.products;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch products");
    }
  }
);

export const getRelatedProducts = createAsyncThunk(
  "products/getRelatedProducts",
  async (productId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/product/getRelatedProducts?productId=${productId}`);
      return response.data.products;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch products");
    }
  }
);

// Fetch a single product by ID
export const fetchProduct = createAsyncThunk(
  "products/fetchProduct",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/product/getProduct`, { params: { id } });
      return response.data.product;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Product not found");
    }
  }
);

// Create a product
export const createProduct = createAsyncThunk(
  "products/createProduct",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await axios.post(`/product/createProduct`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data.product;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to create product");
    }
  }
);

// Update a product
export const updateProduct = createAsyncThunk(
  "products/updateProduct",
  async (updatedData, { rejectWithValue }) => {
    try {
      const response = await axios.put(`/product/updateProduct`, updatedData);
      return response.data.product;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to update product");
    }
  }
);

// Delete a product
export const deleteProduct = createAsyncThunk(
  "products/deleteProduct",
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(`/product/deleteProduct`, { params: { id } });
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to delete product");
    }
  }
);

// Add review to a product
export const addReview = createAsyncThunk(
  "products/addReview",
  async ({ productId, userId, rating, comment }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`/product/addReview`, { productId, userId, rating, comment });
      return response.data.reviews;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to add review");
    }
  }
);

// Add to favorites
export const addToFavorites = createAsyncThunk(
  "products/addToFavorites",
  async (productId, { rejectWithValue }) => {
    try {
      const response = await axios.post(`/product/addToFavorites`, { productId });
      return { productId, favorites: response.data.favorites };
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to add to favorites");
    }
  }
);

// Update click count
export const updateClicks = createAsyncThunk(
  "products/updateClicks",
  async (productId, { rejectWithValue }) => {
    try {
      const response = await axios.post(`/product/updateClicks`, { productId });
      return { productId, clicks: response.data.clicks };
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to update clicks");
    }
  }
);

const productSlice = createSlice({
  name: "products",
  initialState: {
    products: [],
    relatedProducts : [],
    product: null,
    loading: false,
    error: null,
  },
  reducers: {
    clearProduct: (state) => {
      state.product = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.products = action.payload;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(getRelatedProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.relatedProducts = []
      })
      .addCase(getRelatedProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.relatedProducts = action.payload;
      })
      .addCase(getRelatedProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.relatedProducts = []
      })


      .addCase(fetchProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProduct.fulfilled, (state, action) => {
        state.loading = false;
        state.product = action.payload;
      })
      .addCase(fetchProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createProduct.fulfilled, (state, action) => {
        state.products.push(action.payload);
      })
      .addCase(updateProduct.fulfilled, (state, action) => {
        state.products = state.products.map((p) =>
          p.id === action.payload.id ? action.payload : p
        );
      })
      .addCase(deleteProduct.fulfilled, (state, action) => {
        state.products = state.products.filter((p) => p.id !== action.payload);
      })
      .addCase(addReview.fulfilled, (state, action) => {
        if (state.product) {
          state.product.reviews = action.payload;
        }
      })
      .addCase(addToFavorites.fulfilled, (state, action) => {
        const product = state.products.find((p) => p.id === action.payload.productId);
        if (product) product.favorites = action.payload.favorites;
      })
      .addCase(updateClicks.fulfilled, (state, action) => {
        const product = state.products.find((p) => p.id === action.payload.productId);
        if (product) product.clicks = action.payload.clicks;
      });
  },
});

export const { clearProduct } = productSlice.actions;
export default productSlice.reducer;
